import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { adminApi } from '../lib/api/admin'
import { sanitizeHtml } from '../lib/utils/sanitizeHtml'
import './PolicyPage.css'

interface PolicyPageProps {
  policyKey: string
}

interface Policy {
  policy_key: string
  title: string
  body_html: string
}

const SIBLING: Record<string, { label: string; path: string }[]> = {
  terms_conditions:  [{ label: 'Payment Policy', path: '/payment-policy' }, { label: 'Privacy Policy', path: '/privacy-policy' }],
  payment_policy:    [{ label: 'Terms & Conditions', path: '/terms-and-conditions' }, { label: 'Privacy Policy', path: '/privacy-policy' }],
  privacy_policy:    [{ label: 'Terms & Conditions', path: '/terms-and-conditions' }, { label: 'Payment Policy', path: '/payment-policy' }],
  support_page:      [{ label: 'Zoom App Docs', path: '/zoom-docs' }, { label: 'Privacy Policy', path: '/privacy-policy' }],
  zoom_documentation:[{ label: 'Support', path: '/support' }, { label: 'Privacy Policy', path: '/privacy-policy' }],
}

const HEADER_TAG: Record<string, string> = {
  support_page:      'Support',
  zoom_documentation:'Documentation',
}

export default function PolicyPage({ policyKey }: PolicyPageProps) {
  const navigate = useNavigate()
  const [policy, setPolicy]   = useState<Policy | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(false)

  const fetchPolicy = async () => {
    setLoading(true)
    setError(false)
    try {
      const policies: Policy[] = await adminApi.getPublicTermsPolicies()
      const found = policies.find((p) => p.policy_key === policyKey) ?? null
      setPolicy(found)
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchPolicy() }, [policyKey])

  const sibling = SIBLING[policyKey]

  return (
    <div className="policy-page">
      {/* ── Nav ── */}
      <nav className="policy-nav">
        <div className="policy-nav-inner">
          <button className="policy-brand" onClick={() => navigate('/')}>
            <img src="/InterviewDressRehearsal_LOGO.png" alt="IDR" className="policy-nav-logo" />
            <span className="policy-nav-name">Interview Dress Rehearsal</span>
          </button>
          <div className="policy-nav-right">
            <button className="policy-home-link" onClick={() => navigate('/')}>
              ← Back to Home
            </button>
          </div>
        </div>
      </nav>

      {/* ── Body ── */}
      <div className="policy-layout">
        {/* Left sidebar — Also see */}
        {!loading && !error && policy && sibling && sibling.length > 0 && (
          <aside className="policy-sidebar">
            <div className="policy-toc">
              <p className="policy-toc-sibling-label">Also see</p>
              {sibling.map((s) => (
                <button key={s.path} className="policy-toc-sibling-btn" onClick={() => navigate(s.path)}>
                  {s.label} →
                </button>
              ))}
            </div>
          </aside>
        )}

        {/* Main content */}
        <main className="policy-main">
          {loading && (
            <div className="policy-state">
              <div className="policy-spinner" />
              <p>Loading…</p>
            </div>
          )}

          {error && (
            <div className="policy-state">
              <p className="policy-error-msg">Failed to load the page. Please try again.</p>
              <button className="policy-retry-btn" onClick={fetchPolicy}>Retry</button>
            </div>
          )}

          {!loading && !error && !policy && (
            <div className="policy-state">
              <h1 className="policy-not-found-title">Page Not Found</h1>
              <p className="policy-not-found-msg">This policy is not currently available.</p>
              <button className="policy-retry-btn" onClick={() => navigate('/')}>Go to Home</button>
            </div>
          )}

          {!loading && !error && policy && (
            <div className="policy-content">
              <header className="policy-header">
                <div className="policy-header-tag">{HEADER_TAG[policyKey] ?? 'Legal'}</div>
                <h1 className="policy-title">{policy.title}</h1>
                <div className="policy-title-accent" />
              </header>

              <article
                className="policy-body"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(policy.body_html) }}
              />

              {sibling && sibling.length > 0 && (
                <div className="policy-sibling">
                  <span>Also see:</span>
                  {sibling.map((s) => (
                    <button key={s.path} className="policy-sibling-link" onClick={() => navigate(s.path)}>
                      {s.label} →
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {/* ── Footer ── */}
      <footer className="policy-footer">
        <div className="policy-footer-inner">
          <p>&copy; {new Date().getFullYear()} Interview Dress Rehearsal. All rights reserved.</p>
          <div className="policy-footer-links">
            <button onClick={() => navigate('/terms-and-conditions')}>Terms & Conditions</button>
            <span>·</span>
            <button onClick={() => navigate('/payment-policy')}>Payment Policy</button>
            <span>·</span>
            <button onClick={() => navigate('/privacy-policy')}>Privacy Policy</button>
            <span>·</span>
            <button onClick={() => navigate('/support')}>Support</button>
            <span>·</span>
            <button onClick={() => navigate('/zoom-docs')}>Zoom Docs</button>
          </div>
        </div>
      </footer>
    </div>
  )
}
